import React, {useMemo, useState} from 'react';
import {StyleSheet, View} from 'react-native';
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {useTranslation} from 'react-i18next';

import {
  createTelegramInvite,
  deleteTelegramSubscription,
  fetchTelegramRecipients,
  revokeTelegramInvite,
  updateTelegramSubscription,
} from '@/entities/telegram/api';
import type {
  CreateInviteResult,
  TelegramPendingInvite,
  TelegramRecipient,
  TelegramSubscription,
} from '@/entities/telegram/types';
import {shareInvite} from '@/features/manage-telegram-recipients/lib/shareInvite';
import {AppButton} from '@/shared/ui/button';
import {Card, IconBubble} from '@/shared/ui/card';
import {Icon} from '@/shared/ui/icons';
import {SectionHeader} from '@/shared/ui/section';
import {Skeleton} from '@/shared/ui/skeleton';
import {AppText} from '@/shared/ui/text';
import {Toggle} from '@/shared/ui/toggle';
import {radii, spacing, useTheme} from '@/shared/ui/theme';

/**
 * Who gets a Telegram message when this particular person passes a terminal.
 *
 * The recipients screen answers "who receives anything at all" for the whole
 * company; standing on somebody's card, the question is the other way round —
 * "who hears about *him*" — and answering it there meant opening a second screen
 * and reading every recipient's scope list looking for this name.
 *
 * Only person-scoped subscriptions are listed. Company- and department-scoped
 * ones also deliver these events, and the note says so rather than repeating
 * them here: they are not this person's to remove, and offering a button that
 * silently unsubscribes a manager from a whole department would be worse than
 * not showing them.
 */
const styles = StyleSheet.create({
  section: {gap: spacing(2)},
  card: {gap: spacing(2)},
  cardHead: {flexDirection: 'row', alignItems: 'center', gap: spacing(3)},
  grow: {flex: 1},
});

/** One recipient chat paired with its subscription to this person. */
type PersonSub = {recipient: TelegramRecipient; sub: TelegramSubscription};

export function PersonSubscribers({
  companyId,
  recordId,
  personName,
}: {
  companyId: string;
  recordId: string;
  personName: string;
}) {
  const {t} = useTranslation();
  const p = useTheme();
  const qc = useQueryClient();

  const [minted, setMinted] = useState<CreateInviteResult | null>(null);
  const [confirmRemove, setConfirmRemove] = useState<string | null>(null);

  const recipients = useQuery({
    queryKey: ['telegram-recipients', companyId],
    queryFn: () => fetchTelegramRecipients(companyId),
  });

  const invalidate = () =>
    qc.invalidateQueries({queryKey: ['telegram-recipients', companyId]});

  const subs: PersonSub[] = useMemo(() => {
    const out: PersonSub[] = [];
    for (const recipient of recipients.data?.recipients ?? []) {
      for (const sub of recipient.subscriptions) {
        if (sub.scope_type === 'person' && sub.scope_id === recordId) {
          out.push({recipient, sub});
        }
      }
    }
    return out;
  }, [recipients.data, recordId]);

  const pending: TelegramPendingInvite[] = useMemo(
    () =>
      (recipients.data?.pending ?? []).filter(i => {
        if (i.scope_type !== 'person') return false;
        // Older servers answer without `scope_ids`; fall back to the single
        // field so an invite minted before that field existed still shows up.
        const ids = i.scope_ids?.length ? i.scope_ids : [i.scope_id];
        return ids.includes(recordId);
      }),
    [recipients.data, recordId],
  );

  const mint = useMutation({
    mutationFn: () =>
      createTelegramInvite(companyId, {
        // The label is what the manager will see in the recipients list months
        // from now, so it names the subject rather than the (still unknown)
        // reader — nobody has opened the link yet.
        label: personName,
        scope_type: 'person',
        scope_ids: [recordId],
        notify_arrival: true,
        notify_departure: true,
        include_in_digest: false,
        multi_use: false,
      }),
    onSuccess: result => {
      setMinted(result);
      invalidate();
    },
  });

  const toggles = useMutation({
    mutationFn: (v: {
      id: string;
      notify_arrival: boolean;
      notify_departure: boolean;
      include_in_digest: boolean;
    }) =>
      updateTelegramSubscription(companyId, v.id, {
        notify_arrival: v.notify_arrival,
        notify_departure: v.notify_departure,
        include_in_digest: v.include_in_digest,
      }),
    onSuccess: invalidate,
  });

  const remove = useMutation({
    mutationFn: (id: string) => deleteTelegramSubscription(companyId, id),
    onSuccess: () => {
      setConfirmRemove(null);
      return invalidate();
    },
  });

  const revoke = useMutation({
    mutationFn: (code: string) => revokeTelegramInvite(companyId, code),
    onSuccess: invalidate,
  });

  return (
    <View style={styles.section}>
      <SectionHeader title={t('telegram.personSectionTitle')} />

      {recipients.isPending ? (
        <Skeleton height={96} radius={radii.lg} />
      ) : recipients.isError ? (
        <AppText variant="body" color={p.mutedForeground}>
          {t('telegram.error')}
        </AppText>
      ) : (
        <>
          {subs.length === 0 && pending.length === 0 ? (
            <AppText variant="body" color={p.mutedForeground}>
              {t('telegram.personSectionEmpty')}
            </AppText>
          ) : null}

          {subs.map(({recipient, sub}) => (
            <Card key={sub.id} padding={4} style={styles.card}>
              <View style={styles.cardHead}>
                <IconBubble tone={p.accent} size={38}>
                  <Icon name="send" size={18} color={p.primary} />
                </IconBubble>
                <AppText variant="subtitle" style={styles.grow} numberOfLines={1}>
                  {recipient.label || recipient.title || recipient.chat_id}
                </AppText>
              </View>
              <Toggle
                label={t('telegram.notifyArrival')}
                value={sub.notify_arrival}
                onChange={v =>
                  toggles.mutate({
                    id: sub.id,
                    notify_arrival: v,
                    notify_departure: sub.notify_departure,
                    include_in_digest: sub.include_in_digest,
                  })
                }
              />
              <Toggle
                label={t('telegram.notifyDeparture')}
                value={sub.notify_departure}
                onChange={v =>
                  toggles.mutate({
                    id: sub.id,
                    notify_arrival: sub.notify_arrival,
                    notify_departure: v,
                    include_in_digest: sub.include_in_digest,
                  })
                }
              />
              {confirmRemove === sub.id ? (
                <AppButton
                  title={t('telegram.confirmRemove')}
                  tone="destructive"
                  size="sm"
                  loading={remove.isPending}
                  onPress={() => remove.mutate(sub.id)}
                />
              ) : (
                <AppButton
                  title={t('telegram.remove')}
                  tone="ghost"
                  size="sm"
                  onPress={() => setConfirmRemove(sub.id)}
                />
              )}
            </Card>
          ))}

          {pending.map(invite => (
            <Card key={invite.code} padding={4} style={styles.card}>
              <View style={styles.cardHead}>
                <IconBubble tone={p.warningSoft} size={38}>
                  <Icon name="link" size={18} color={p.warning} />
                </IconBubble>
                <View style={styles.grow}>
                  <AppText variant="subtitle" numberOfLines={1}>
                    {invite.label || t('telegram.newInvite')}
                  </AppText>
                  <AppText variant="caption">{t('telegram.pendingHint')}</AppText>
                </View>
              </View>
              <AppButton
                title={t('telegram.share')}
                icon="send"
                size="sm"
                onPress={() =>
                  void shareInvite(t, {
                    deepLink: invite.deep_link,
                    code: invite.code,
                  })
                }
              />
              <AppButton
                title={t('telegram.revoke')}
                tone="ghost"
                size="sm"
                loading={revoke.isPending}
                onPress={() => revoke.mutate(invite.code)}
              />
            </Card>
          ))}
        </>
      )}

      {minted ? (
        <Card padding={4} style={[styles.card, {borderColor: p.primary, borderWidth: 1}]}>
          <AppText variant="subtitle">{personName}</AppText>
          <AppText variant="mono" selectable>
            {minted.deep_link || t('telegram.codeOnly', {code: minted.code})}
          </AppText>
          <AppButton
            title={t('telegram.share')}
            icon="send"
            onPress={() =>
              void shareInvite(t, {deepLink: minted.deep_link, code: minted.code})
            }
          />
          <AppButton
            title={t('actions.done')}
            tone="ghost"
            onPress={() => setMinted(null)}
          />
        </Card>
      ) : (
        <AppButton
          title={t('telegram.personAddRecipient')}
          tone="soft"
          icon="plus"
          loading={mint.isPending}
          onPress={() => mint.mutate()}
        />
      )}

      <AppText variant="caption">{t('telegram.personScopeNote')}</AppText>
    </View>
  );
}
